<template>
  <div id="app" class="h-100" :class="[skinClasses]">
    <component :is="layout">
      <router-view />
    </component>
    <!-- Global Responsive Tab Selector - Rendered in portal-target-tabs div -->
    <div v-if="tabs && tabs.length > 0" id="global-tab-dropdown-source" style="display: none;">
      <div class="global-responsive-tab-selector">
        <b-dropdown 
          block
          :text="selectedTab.name ? selectedTab.name : (tabs.length > 0 ? selectedTab : 'Select Tab')"
          variant="primary"
          class="responsive-tab-dropdown"
          :popper-opts="{ placement: 'bottom-start' }"
          boundary="viewport"
          :no-flip="false"
        >
          <b-dropdown-item 
            v-for="tab in tabs" 
            :key="tab.id" 
            @click="onGlobalTabChange(tab)"
            :active="tab.id === selectedTabId"
          >
            {{ tab.name }}
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>

    <scroll-to-top v-if="enableScrollToTop" />
  </div>
</template>

<script>
import ScrollToTop from "@core/components/scroll-to-top/ScrollToTop.vue";
import { isUserLoggedIn } from "@/auth/utils";
import { mapActions } from "vuex";

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from "@themeConfig";
import { provideToast } from "vue-toastification/composition";
import { watch } from "@vue/composition-api";
import useAppConfig from "@core/app-config/useAppConfig";

import { useWindowSize, useCssVar } from "@vueuse/core";

import store from "@/store";
import {
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'
const LayoutVertical = () => import("@/layouts/vertical/LayoutVertical.vue");
const LayoutHorizontal = () =>
  import("@/layouts/horizontal/LayoutHorizontal.vue");
const LayoutFull = () => import("@/layouts/full/LayoutFull.vue");
const LayoutAdmission = () => import("@/layouts/admission/LayoutAdmission.vue");

export default {
  components: {
    BDropdown,
    BDropdownItem,
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,
    LayoutAdmission,

    ScrollToTop,
  },

  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === "full") return "layout-full";
      if (this.$route.meta.layout === "admission") return "layout-admission";
      if (this.authUser().id || this.$route.meta.guest) {
        return `layout-${this.contentLayoutType}`;
      }
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type;
    },
    currentBreakPoint() {
      if (this.$store.getters['app/currentBreakPoint']) {
        return this.$store.getters['app/currentBreakPoint']
      }
    },
    width() {
      if (this.$store.getters['app/width']) {
        return this.$store.getters['app/width']
      }
    },
    tabNum() {
      return this.$route.query.tabNum || 0
    },
    tabs() {
      return this.$store.getters['app/dropArray'] || []
    },
    selectedTabId() {
      return this.$store.getters['app/selectedTabId'] || 0
    },
    selectedTab() {
      if (this.tabs && this.tabs.length > 0) {
        const found = this.tabs.find(t => t.id === this.selectedTabId)
        if (found) {
          return found
        }
        // If no tab matches selectedTabId, return first tab
        return this.tabs[0]
      }
      return { id: 0, name: 'Select Tab' }
    },
    tabsContainerExists() {
      // Check if .tabs container exists in DOM
      return !!document.querySelector('.tabs')
    },
  },
  beforeUpdate() {
  },
  updated() {
    // Removed problematic setInterval that was assigning to computed property
  },
  watch: {
    $route(to, from) {
      if (
        this.authUser() &&
        this.authUser().type &&
        this.authUser().type.name == "Applicant"
      ) {
        if(to.name != 'applicant-information-form' && to.name !== 'applyForm-success' && to.name != 'credit' && to.name != 'dashboard' && to.name != 'error-404'){
          this.$router.push({
            name: "admissionForm",
            params: {
              id: this.authApplicant()?.id,
              type: this.authApplicant()?.applicant_type_id,
              secretKey: this.authApplicant()?.secret,
              name: "nationality",
            },
          });
        }
      }
      this.$nextTick(() => {
        this.setPageTabs()
      })
    },
    tabNum() {
      this.showTabList()
    },
    tabs(newTabs) {
      // When tabs are populated, ensure first tab is selected if none selected
      if (newTabs && newTabs.length > 0 && this.selectedTabId === 0) {
        this.$nextTick(() => {
          if (!this.$route.query.tabNum) {
            this.setSelectedTab(newTabs[0])
          }
        })
      }
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = [
      "primary",
      "secondary",
      "success",
      "info",
      "warning",
      "danger",
      "light",
      "dark",
    ];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(
        `--${colors[i]}`,
        document.documentElement
      ).value.trim();
    }

    // Set Theme Breakpoints
    const breakpoints = ["xs", "sm", "md", "lg", "xl"];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
        useCssVar(
          `--breakpoint-${breakpoints[i]}`,
          document.documentElement
        ).value.slice(0, -2)
      );
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResizeGlobal)
  },
  mounted() {
    this.handleResizeGlobal()
    window.addEventListener('resize', this.handleResizeGlobal)
    this.getTempFavicon()
    this.getUniversityData();
    if (this.authUser().id && isUserLoggedIn()) {
      this.getUserLiveData();
    }
    if (this.authUser().id && isUserLoggedIn()) {
      setInterval(() => {
        this.getUserLiveData();
      }, 180000);
    }
    // Auto-detect tabs on initial page load
    this.$nextTick(() => {
      setTimeout(() => {
        this.setPageTabs()
      }, 5000)
    })
  },
  methods: {
    ...mapActions({
      updateTabsFilter: 'app/updateTabsFilter',
    }),
    setSelectedTab(tab) {
      if (tab && tab.id !== undefined) {
        this.$store.commit('app/SET_SELECTED_TAB_ID', tab.id)
      }
    },
    onGlobalTabChange(tab) {
      if (!tab) return
      
      // Update store
      this.setSelectedTab(tab)
      
      // Update URL query parameter
      if (this.$route.query.tabNum != tab.id) {
        this.$router.replace({
          query: { ...this.$route.query, tabNum: tab.id }
        }).catch(() => {})
      }
    },
    eventHandler() {
      if (this.$route.name == 'auth-login') {
        clearTimeout(this.logoutTimeoutID)
      }
      clearTimeout(this.logoutTimeoutID)
      this.logoutTimeoutID = setTimeout(this.callTimeoutFunc, 10800 * 1000)
    },
    callTimeoutFunc() {
      this.$store.dispatch('users/logout')
          .then((_) => {
            this.$router.push({ name: 'auth-login' })
          })
    },
    async init() {
      const parentElement = document.querySelector('.nav-tabs')
      const tabs = document.querySelector('.tabs')
      const selectedElement = document.querySelector(
          '#idElementCreate.custom_element_create'
      )
      if (tabs) {
        if (this.width <= 995) {
          if (selectedElement) {
            parentElement.parentNode.insertBefore(
                selectedElement,
                parentElement
            )
            selectedElement.classList.remove('custom_none')
          }
        } else {
          if (selectedElement) {
            selectedElement.classList.add('custom_none')
          }
        }
      } else {
        if (selectedElement) {
          selectedElement.classList.add('custom_none')
        }
      }
      await this.getTabs()
    },
    getTabs() {
      let dropArray = []
      let tab = document.querySelectorAll(
          '.tabs .nav-hidden .nav-tabs.flex-column li'
      )
      if (tab) {
        tab.forEach((element, i) => {
          this.dropTabs[i] = {
            name: element.innerText,
            id: i
          }
        })
      }
      return this.dropTabs
    },
    showTabList(e) {
      // alert('')
      // if (e) e.preventDefault();
      // const tabList = document.querySelector('.tabs .nav-hidden .nav-tabs.flex-column');
      // if (tabList) {
      //   if (tabList.classList.contains('visible_class')) {
      //     tabList.classList.remove('visible_class');
      //   } else {
      //     tabList.classList.add('visible_class');
      //   }
      // }
      // return false;
      // let offsets = document.querySelector('.tabs .tab-pane.active').getBoundingClientRect()
      // // let left = document.querySelectorAll('.tabs .tab-pane')[0].getBoundingClientRect()
      // window.scrollTo(offsets.left, offsets.top);
    },
  },
  setup() {
    const { skin, skinClasses } = useAppConfig();
    const { enableScrollToTop } = $themeConfig.layout;

    // If skin is dark when initialized => Add class to body
    if (skin.value === "dark") document.body.classList.add("dark-layout");

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: "Vue-Toastification__fade",
    });

    // Set Window Width in store
    store.commit("app/UPDATE_WINDOW_WIDTH", window.innerWidth);
    const { width: windowWidth } = useWindowSize();
    watch(windowWidth, (val) => {
      store.commit("app/UPDATE_WINDOW_WIDTH", val);
    });

    return {
      skinClasses,
      enableScrollToTop,
    };
  },
};
</script>

<style lang="scss">
.per-page-selector .vs__dropdown-toggle {
  min-width: 80px !important;
}
.per-page-selector .vs__dropdown-menu {
  top: calc(-400% + 1rem) !important;
}
[dir="rtl"] .vs__search,
[dir="rtl"] .vs__search:focus {
  text-align: right;
}
.year-picker .year-picker__input--real {
  height: 37px !important;
}
.year-picker .year-picker__input {
  width: 100% !important;
}
.year-picker {
  width: 100% !important;
  margin-left: 0 !important;
}
html[dir="rtl"]
  .vue-form-wizard
  .wizard-card-footer
  .wizard-footer-right
  .wizard-btn::after {
  -webkit-transform: rotate(180deg);
  -moz-transform: rotate(180deg);
  -ms-transform: rotate(180deg);
  -o-transform: rotate(180deg);
  transform: rotate(180deg);
  display: inline-block;
}
.no_hover {
  &.btn-flat-primary:hover:not(.disabled):not(:disabled) {
    background-color: transparent !important;
    color: #746d69;
  }
  &.btn-flat-primary {
    color: #746d69;
  }
}
.vue-form-wizard {
  box-shadow: none !important;
}
.vue-form-wizard
  .wizard-navigation
  .wizard-nav
  li.active
  a
  .wizard-icon-circle {
  .wizard-icon {
    color: #dae1e7 !important;
  }
}
.wizard-nav.wizard-nav-pills li {
  padding-bottom: 0.5rem !important;
}
.vue-form-wizard .wizard-navigation .wizard-nav li a .wizard-icon-circle {
  box-shadow: none !important;
  margin-right: 0.2rem !important;
  width: auto !important;
  background-color: transparent !important;
  height: auto;
  .wizard-icon {
    color: #746d69 !important;
  }
  .wizard-icon-container {
    border-radius: 0px;
  }
}
.wizard-navigation .wizard-nav.wizard-nav-pills li a {
  background-color: transparent !important;
  border-color: #ededed #ededed #dae1e7;
  border-radius: 0.2rem;
  padding: 0.786rem 1rem;
  .wizard-icon-circle {
    // display: none;
  }
  span {
    font-weight: normal !important;
    color: #746d69;
  }
}
.wizard-navigation .wizard-nav.wizard-nav-pills li.active a {
  background-color: #054978 !important;
  border-color: #054978 !important;
  border-radius: 0.2rem;
  padding: 0.786rem 1rem;
  .wizard-icon-circle {
    // display: none;
  }
  span {
    color: #fff !important;
    font-weight: normal !important;
  }
}
[dir="ltr"] .vertical.wizard-vertical.vue-form-wizard .wizard-card-footer {
  margin-left: 259px !important;
}
[dir="rtl"] .vertical.wizard-vertical.vue-form-wizard .wizard-card-footer {
  margin-right: 258px !important;
}
.vertical.wizard-vertical.vue-form-wizard .wizard-navigation .wizard-nav {
  min-width: 260px !important;
}
.custom_drop {
  .dropdown-menu {
    width: 100%;
    top: 140px !important;
  }
  .dropdown {
    .dropdown-toggle {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }
}
@media screen and (max-width: 980px) {
  .nav.nav-tabs {
    display: -webkit-inline-box;
    width: 100%;
    flex-wrap: nowrap;
    overflow: scroll;
  }
}
@media screen and (max-width: 800px) {
  .custom_slider {
    display: none;
  }
}
@media screen and (max-width: 680px) {
  .mt-sm-2 {
    margin-top: 4px;
  }
}
.custom_search {
  width: 50%;
}
.custom_link:hover {
  text-decoration: underline;
}
.custom_hover:hover {
  color: #000;
}
[dir="ltr"] .right_left {
  direction: rtl !important;
}
.left_right {
  direction: ltr !important;
}
.left_right > * {
  direction: ltr !important;
}
.custom_invalid {
  .input-group-text,
  .vs1__combobox,
  .vs__dropdown-toggle {
    border-color: #ea5455;
  }
}
.top_100 {
  .dropdown-menu {
    top: 100px !important;
  }
}
.disabled_all {
  pointer-events: none;
  opacity: 0.2;
  cursor: not-allowed;
}
html {
  scroll-behavior: smooth;
}
.card {
  box-shadow: none !important;
}
.header-navbar.navbar-shadow {
  box-shadow: none !important;
}
.gap {
  gap: 0.5rem;
}
[dir="ltr"]
  .table.b-table.table-sm
  > thead
  > tr
  > [aria-sort]:not(.b-table-sort-icon-left),
[dir="ltr"]
  .table.b-table
  > tfoot
  > tr
  > [aria-sort]:not(.b-table-sort-icon-left) {
  background-position: left calc(1rem / 4) center !important;
}
[dir="ltr"]
  .table.b-table
  > thead
  > tr
  > [aria-sort]:not(.b-table-sort-icon-left),
[dir="ltr"]
  .table.b-table
  > tfoot
  > tr
  > [aria-sort]:not(.b-table-sort-icon-left) {
  background-position: left calc(2.5rem / 3) center !important;
}
[dir="rtl"]
  .table.b-table
  > thead
  > tr
  > [aria-sort]:not(.b-table-sort-icon-left),
[dir="ltr"]
  .table.b-table
  > tfoot
  > tr
  > [aria-sort]:not(.b-table-sort-icon-left) {
  background-position: right calc(2.5rem / 2) center !important;
}
.w-80 {
  width: 250px;
}
.mask_gradient_left {
  -webkit-mask-image: linear-gradient(to left, black 70%, transparent 120%);
  mask-image: linear-gradient(to left, black 70%, transparent 120%);
}
.mask_gradient_right {
  -webkit-mask-image: linear-gradient(to right, black 73%, transparent 100%);
  mask-image: linear-gradient(to right, black 73%, transparent 100%);
}

.custom-file-label {
  cursor: pointer !important;
}

.nav-pills .nav-link,
.nav-tabs .nav-link {
  justify-content: start !important;
}
.nav-item .nav-link.active {
  border-color: #054978 !important;
  background-color: #054978 !important;
  box-shadow: none;
  text-align: start;
  background-color: transparent;
  border: 1px solid transparent;
  padding: 0.786rem 1.5rem;
  border-radius: 0.2rem;
  margin: 0;
  color: #fff !important;
  font-weight: 500;
  display: block;
  width: 100%;
  display: inline-block;
  font-weight: 400;
  color: #746d69;
  vertical-align: middle;
  user-select: none;
  font-size: 1rem;
  line-height: 1;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out, background 0s,
    border 0s;
  text-transform: none;
  font-family: inherit;
}
.nav-hidden ul li a::after {
  display: none;
}
.cover {
  background-size: cover;
}
.pointer {
  cursor: pointer !important;
}
.mr-05 {
  margin-right: 5px;
}
.mx-05 {
  margin-right: 5px;
  margin-left: 5px;
}
.customWidth {
  width: 100px;
  margin: 0px;
  text-align: end;
}
.customDetailsWidth {
  width: 150px;
  margin: 0px;
  text-align: end;
}
.customWidth div,
.customDetailsWidth div {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  padding-inline-end: 9px;
}
.customAction {
  text-align: end;
}
.customAction div {
  margin: 0px 1rem;
}
#myHeader {
  position: fixed;
  z-index: 999;
  margin-top: -15px;
  min-height: auto !important;
}
.sticky {
  background: #fff;
  margin-top: -35px !important;
}
.main-menu.menu-light .navigation > li ul li > a {
  padding-left: 10px !important;
}

.modal .modal-header .close {
  margin: 0;
}

.vs--single {
  .vs__selected-options {
    width: 0;
  }

  .vs__selected {
    display: inline-block;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 100%;
    overflow: hidden;
  }
}

// .vs__dropdown-toggle{
//   height: 100% !important;
// }

/* Global Responsive Tab Selector Styles */
.portal-target-tabs {
  display: none;
  margin-bottom: 1rem;
}

.portal-target-tabs .global-responsive-tab-selector {
  display: block;
}

/* Show on mobile/tablet only */
@media (max-width: 981px) {
  .portal-target-tabs {
    display: block !important;
  }
  
  /* Hide old custom_drop on mobile */
  .custom_drop {
    display: none !important;
  }
  
  /* Hide vertical tabs on mobile */
  .tabs .nav-hidden .nav-tabs.flex-column {
    display: none !important;
  }
}

/* Hide on desktop */
@media (min-width: 982px) {
  .global-responsive-tab-selector {
    display: none !important;
  }
}

/* Style the b-dropdown in global selector */
.portal-target-tabs .global-responsive-tab-selector {
  position: relative;
}

.portal-target-tabs .dropdown,
.portal-target-tabs .btn-group {
  position: relative;
  display: block;
}

.portal-target-tabs .dropdown-toggle {
  width: 100%;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  font-weight: 500;
  color: white;
}

.portal-target-tabs .dropdown-menu {
  width: 100%;
  max-height: 400px;
  z-index: 1050;
  position: absolute;
  top: 100%;
  left: 0;
  right: auto;
  margin-top: 0.125rem;
  transform: scale(1, 1) !important;  /* Override BootstrapVue animation */
  transform-origin: top !important;
}

.portal-target-tabs .dropdown-menu:not(.show) {
  display: none;
}

.portal-target-tabs .dropdown-menu.show {
  display: block;
  transform: scale(1, 1) !important;  /* Ensure full scale when shown */
}

.portal-target-tabs .dropdown-item {
  padding: 0.65rem 1.28rem;
  cursor: pointer;
}

</style>
